import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PaperPlaneRightIcon, ChatCircleDotsIcon, ImageSquareIcon } from "@phosphor-icons/react";
import { fmtTime } from "@/lib/tz";

/**
 * Two-way message thread on a job/issue.
 * Used by driver in their portal and by mechanic in the Jobs page.
 */
export default function JobMessages({ jobId, currentUserId, currentRole }) {
  const [data, setData] = useState({ messages: [], attachments: [], title: "", status: "" });
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = async () => {
    if (!jobId) return;
    try {
      const { data } = await api.get(`/jobs/${jobId}/messages`);
      setData(data);
    } catch (e) {
      // silent — panel just shows empty
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000); // poll every 5s
    return () => clearInterval(t);
  }, [jobId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [data.messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/jobs/${jobId}/messages`, { text: text.trim() });
      setText("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const isMine = (m) => m.from_id === currentUserId;
  const roleBadge = (r) => (r === "driver" ? "DRIVER" : r === "mechanic" ? "MECH" : r === "admin" ? "ADMIN" : (r || "").toUpperCase());

  return (
    <div className="bg-white border border-slate-300 rounded-sm flex flex-col" data-testid={`job-messages-${jobId}`}>
      <div className="px-4 py-3 border-b border-slate-300 flex items-center gap-2">
        <ChatCircleDotsIcon size={16} weight="bold" className="text-blue-600" />
        <div className="font-display font-bold text-sm tracking-tight text-slate-900">Conversation</div>
        <div className="ml-auto text-[10px] font-mono uppercase tracking-wider text-slate-500">{data.messages.length} messages</div>
      </div>

      {/* Attachments strip */}
      {data.attachments?.length > 0 && (
        <div className="px-4 py-2 border-b border-slate-300 flex items-center gap-2 overflow-x-auto scrollbar-thin">
          <ImageSquareIcon size={14} className="text-blue-600 flex-shrink-0" />
          {data.attachments.map((a, i) => (
            <a
              key={`${a.name}-${i}`}
              href={a.data_base64 ? `data:${a.type || "application/octet-stream"};base64,${a.data_base64}` : "#"}
              download={a.name}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0"
              data-testid={`issue-attachment-${i}`}
            >
              {a.type?.startsWith("image/") && a.data_base64 ? (
                <img
                  src={`data:${a.type};base64,${a.data_base64}`}
                  alt={a.name}
                  className="w-12 h-12 object-cover rounded-sm border border-slate-300 hover:border-blue-500"
                />
              ) : (
                <div className="px-2 py-1 bg-slate-200 text-xs text-slate-600 rounded-sm font-mono truncate max-w-[140px]">
                  {a.name}
                </div>
              )}
            </a>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin" style={{ maxHeight: 340 }}>
        {data.messages.length === 0 && (
          <div className="text-center text-slate-500 text-xs font-mono py-6">
            No messages yet. Say hello 👋
          </div>
        )}
        {data.messages.map((m) => (
          <div key={m.id} className={`flex ${isMine(m) ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
            <div className={`max-w-[75%] px-3 py-2 rounded-sm text-sm ${isMine(m) ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-800"}`}>
              <div className="text-[10px] font-mono uppercase tracking-wider opacity-70 mb-0.5">
                {m.from_name} · {roleBadge(m.from_role)} · {fmtTime(m.ts)}
              </div>
              <div className="whitespace-pre-wrap leading-snug">{m.text}</div>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={send} className="p-3 border-t border-slate-300 flex gap-2">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a message..."
          className="flex-1 px-3 py-2 rounded-sm font-mono text-sm"
          data-testid={`msg-input-${jobId}`}
        />
        <Button
          type="submit"
          disabled={!text.trim() || sending}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold"
          data-testid={`msg-send-${jobId}`}
        >
          <PaperPlaneRightIcon size={14} weight="bold" />
        </Button>
      </form>
    </div>
  );
}
